import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn('Authentication expired or invalid');
      // Clear any stale auth state and redirect to login
      localStorage.removeItem('user');
      // Only redirect if not already on the landing page
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const createSession = (sessionId) => 
  api.post('/auth/session', { session_id: sessionId });

export const getMe = () => 
  api.get('/auth/me');

export const updateRole = (role) => 
  api.post('/auth/role', { role });

export const logout = () => 
  api.post('/auth/logout');

// Classrooms
export const getClassrooms = () => 
  api.get('/classrooms');

export const getClassroom = (classroomId) => 
  api.get(`/classrooms/${classroomId}`);

export const createClassroom = (data) => 
  api.post('/classrooms', data);

export const joinClassroom = (classCode) => 
  api.post('/classrooms/join', { class_code: classCode });

// Standards
export const getStandards = () => 
  api.get('/standards');

export const getGradeStandards = (grade) => 
  api.get(`/standards/${grade}`);

// Exercises
export const getExercises = (classroomId = null) => 
  api.get('/exercises', { params: classroomId ? { classroom_id: classroomId } : {} });

export const getExercise = (exerciseId, sessionId = null) => 
  api.get(`/exercises/${exerciseId}`, { params: sessionId ? { session_id: sessionId } : {} });

export const createExercise = (data) => 
  api.post('/exercises', data);

export const createExerciseFromTemplate = (data) => 
  api.post('/exercises/from-template', data);

export const deleteExercise = (exerciseId) =>
  api.delete(`/exercises/${exerciseId}`);

export const startExercise = (exerciseId) => 
  api.post(`/exercises/${exerciseId}/start`);

export const saveProgress = (exerciseId, typedText, timeSpentSeconds) =>
  api.post(`/exercises/${exerciseId}/save-progress`, {
    typed_text: typedText,
    time_spent_seconds: timeSpentSeconds
  });

export const updateExerciseStatus = (exerciseId, status) =>
  api.post(`/exercises/${exerciseId}/update-status`, { status });

export const submitExercise = (exerciseId, data) => 
  api.post(`/exercises/${exerciseId}/submit`, data);

export const submitQuiz = (exerciseId, sessionId, answers, tabSwitches = 0) => 
  api.post(`/exercises/${exerciseId}/quiz`, { session_id: sessionId, answers, tab_switches: tabSwitches });

// Templates
export const getTemplates = () => 
  api.get('/templates');

export const getSharedTemplates = () =>
  api.get('/templates/shared');

export const createTemplate = (data) => 
  api.post('/templates', data);

export const createTemplateFromExercise = (exerciseId) => 
  api.post(`/templates/from-exercise/${exerciseId}`);

export const deleteTemplate = (templateId) => 
  api.delete(`/templates/${templateId}`);

export const updateTemplateSharing = (templateId, shareSettings) =>
  api.put(`/templates/${templateId}/share`, shareSettings);

// Teachers (for sharing)
export const getTeachers = () =>
  api.get('/teachers');

// Progress & Reports
export const getProgress = () => 
  api.get('/progress');

export const getClassroomReport = (classroomId) => 
  api.get(`/reports/classroom/${classroomId}`);

export const getClassroomLiveStatus = (classroomId) =>
  api.get(`/classrooms/${classroomId}/live-status`);

export const getClassroomObjectivesReport = (classroomId) =>
  api.get(`/reports/classroom/${classroomId}/objectives`);

export const getClassroomAssignmentsReport = (classroomId) =>
  api.get(`/reports/classroom/${classroomId}/assignments`);

export const getClassroomObjectivesMatrix = (classroomId) =>
  api.get(`/reports/classroom/${classroomId}/objectives-matrix`);

// Badges
export const getBadges = () => 
  api.get('/badges');

// Leaderboards
export const getClassLeaderboard = (classroomId) => 
  api.get(`/leaderboards/class/${classroomId}`);

export const getSchoolLeaderboard = () =>
  api.get('/leaderboards/school');

// Retype/Reset
export const allowRetype = (exerciseId, studentId, reason) =>
  api.post(`/exercises/${exerciseId}/allow-retype`, { student_id: studentId, reason });

export const allowQuizRetake = (exerciseId, studentId, reason) =>
  api.post(`/exercises/${exerciseId}/allow-quiz-retake`, { student_id: studentId, reason });

export const getRetypeHistory = (exerciseId, studentId) =>
  api.get(`/exercises/${exerciseId}/retype-history/${studentId}`);

// Objective Stats (Teacher)
export const getObjectiveStats = () => 
  api.get('/objectives/stats');

export const getClassroomObjectiveStats = (classroomId) => 
  api.get(`/objectives/stats/${classroomId}`);

// Exercise Reports (Teacher)
export const getExerciseReport = (exerciseId) =>
  api.get(`/reports/exercise/${exerciseId}`);

export const getStudentExerciseDetail = (studentId, exerciseId) =>
  api.get(`/reports/student/${studentId}/exercise/${exerciseId}`);

// Question Analytics (Teacher)
export const getQuestionAnalytics = (classroomId = null) =>
  api.get('/analytics/questions', { params: classroomId ? { classroom_id: classroomId } : {} });

// Student Objectives Performance
export const getStudentObjectivesPerformance = (studentId, classroomId = null) =>
  api.get(`/analytics/student/${studentId}/objectives`, { params: classroomId ? { classroom_id: classroomId } : {} });

// Objective Struggling Students
export const getObjectiveStrugglingStudents = (objectiveCode, classroomId = null) =>
  api.get(`/analytics/objective/${encodeURIComponent(objectiveCode)}/struggling-students`, { params: classroomId ? { classroom_id: classroomId } : {} });

// Admin
export const getAdminUsers = () => 
  api.get('/admin/users');

export const updateAdminUser = (userId, data) => 
  api.patch(`/admin/users/${userId}`, data);

export const getAdminStats = () => 
  api.get('/admin/stats');

export default api;
